import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, GraduationCap, Home as HomeIcon, Stethoscope, HeartHandshake, Building, Lightbulb, MapPin } from "lucide-react";

const Home = () => {
  const objectives = [
    { icon: Heart, title: "Combating Poverty", description: "Working to alleviate poverty and improve living conditions for vulnerable communities." },
    { icon: Stethoscope, title: "Providing Healthcare", description: "Ensuring access to quality healthcare services for those in need." },
    { icon: Users, title: "Supporting Children", description: "Nurturing and empowering children to reach their full potential." },
    { icon: HeartHandshake, title: "Supporting the Elderly", description: "Caring for our senior citizens with dignity and compassion." },
  ];

  const campaigns = [
    {
      id: "thousand-smiles",
      image: "/10thdecember/img1.jpeg",
      title: "A Thousand Smiles - IDP Camp Feeding Initiative",
      date: "December 10, 2025",
      excerpt: "A successful feeding initiative at Area 1 IDP Camp, Abuja. We brought smiles, food, clothing, and essential supplies to displaced families.",
    },
    {
      id: "lee-royalty-orphanage",
      image: "/leeroyalty/img1.jpg",
      title: "Lee Royalty Orphanage Center Outreach",
      date: "February 22, 2026",
      excerpt: "A day of love, hope, and meaningful impact at Lee Royalty Orphanage Center, Benin City, Edo State.",
    },
    {
      id: "charity-faith-hope",
      image: "/charityfaith/img1.jpeg",
      title: "Charity Faith and Hope Orphanage Visit",
      date: "April 15, 2025",
      excerpt: "Our team visited Charity Faith and Hope Orphanage Home to spend quality time with the children and share love.",
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-cream min-h-[90vh] overflow-hidden">
        <div className="absolute top-20 right-1/4 w-3 h-3 bg-primary rounded-full"></div>
        <div className="absolute top-40 right-1/3 w-2 h-2 bg-red-500 rounded-full"></div>
        <div className="absolute bottom-40 left-1/4 w-2 h-2 bg-green-500 rounded-full"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="z-10">
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
                Changing
                <br />
                Lives with
                <br />
                <span className="text-primary">Compassion</span>
              </h1>
              <p className="mt-6 text-gray-600 text-lg max-w-md">
                Oshoke Alasa Foundation is dedicated to combating poverty, providing healthcare, and supporting communities across Nigeria.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/donate"
                  className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-medium text-sm transition-colors inline-flex items-center gap-2"
                >
                  DONATE NOW
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/about"
                  className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 rounded-full font-medium text-sm transition-colors"
                >
                  LEARN MORE
                </Link>
              </div>
            </div>

            <div className="relative flex justify-center lg:justify-end">
              <div className="relative w-[400px] h-[500px] md:w-[450px] md:h-[550px]">
                <div className="absolute top-8 left-1/2 transform -translate-x-1/2 w-48 h-48 rounded-full overflow-hidden border-4 border-white shadow-xl">
                  <img src="/gallery/img1.jpeg" alt="Foundation work" className="w-full h-full object-cover" />
                </div>
                <div className="absolute top-48 right-4 w-40 h-40 rounded-full overflow-hidden border-4 border-white shadow-xl">
                  <img src="/gallery/img7.jpeg" alt="Community outreach" className="w-full h-full object-cover" />
                </div>
                <div className="absolute bottom-20 left-8 w-36 h-36 rounded-full overflow-hidden border-4 border-white shadow-xl">
                  <img src="/gallery/img12.jpeg" alt="Healthcare support" className="w-full h-full object-cover" />
                </div>
                <div className="absolute bottom-32 right-16 w-20 h-20 bg-primary rounded-full opacity-80"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Aims & Objectives */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-primary font-medium text-sm tracking-wider uppercase mb-3">WHAT WE DO</p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Aims & Objectives</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              We are committed to making a lasting difference in the lives of those who need it most.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {objectives.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                <div className="w-20 h-20 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-10 h-10 text-primary" />
                </div>
                <h3 className="font-semibold text-xl text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { icon: GraduationCap, title: "Providing Scholarships" },
              { icon: Building, title: "Educational Development" },
              { icon: MapPin, title: "Community Development" },
              { icon: Lightbulb, title: "Promoting Entrepreneurship" },
              { icon: HomeIcon, title: "Supporting Local Communities" },
            ].map((item, index) => (
              <div key={index} className="bg-cream rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <item.icon className="w-8 h-8 text-primary mx-auto mb-3" />
                <p className="font-medium text-gray-900 text-sm">{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="w-80 h-80 md:w-96 md:h-96 rounded-full overflow-hidden border-8 border-white shadow-2xl mx-auto lg:mx-0">
                <img src="/gallery/img20.jpeg" alt="Foundation team" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-8 right-0 lg:right-8 w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-8 border-white shadow-2xl">
                <img src="/gallery/img25.jpeg" alt="Community work" className="w-full h-full object-cover" />
              </div>
            </div>

            <div className="lg:pl-8">
              <p className="text-primary font-medium text-sm tracking-wider uppercase mb-3">ABOUT US</p>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Changing Lives with<br />Knowledge & Love
              </h2>
              <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
                <p className="text-gray-600 leading-relaxed mb-4">
                  <span className="font-semibold text-gray-900">Our promise is that your giving is doing what it's supposed to — changing lives.</span>
                </p>
                <p className="text-gray-500 text-sm leading-relaxed">
                  The Oshoke Alasa Foundation is dedicated to creating lasting positive change in communities across Nigeria. Through our various programs, we support children, the elderly, and vulnerable populations with healthcare, education, and essential resources.
                </p>
              </div>
              <Link
                to="/about"
                className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-full font-medium text-sm transition-colors inline-flex items-center gap-2"
              >
                LEARN MORE
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Campaigns */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-primary font-medium text-sm tracking-wider uppercase mb-3">OUR IMPACT</p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">Recent Campaigns</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              See how we're making a difference in communities across Nigeria.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {campaigns.map((campaign, index) => (
              <Link key={index} to={`/campaigns/${campaign.id}`} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 group">
                <div className="relative h-56 overflow-hidden">
                  <img src={campaign.image} alt={campaign.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute top-4 left-4 bg-primary text-white text-xs font-medium px-3 py-1 rounded-full">
                    {campaign.date}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-semibold text-xl text-gray-900 mb-3 group-hover:text-primary transition-colors">{campaign.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{campaign.excerpt}</p>
                  <span className="text-primary font-medium text-sm inline-flex items-center gap-2">
                    Read More <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/campaigns" className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 rounded-full font-medium text-sm transition-colors inline-flex items-center gap-2">
              VIEW ALL CAMPAIGNS
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { number: "2500+", label: "Lives Impacted" },
              { number: "10+", label: "Campaigns Completed" },
              { number: "50+", label: "Volunteers" },
              { number: "5+", label: "Communities Served" },
            ].map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-4xl font-bold text-primary mb-2">{stat.number}</p>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
            Join Us in Making a Difference
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Your support helps us continue our mission of changing lives and building stronger communities.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate" className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-full font-medium text-sm transition-colors">
              DONATE NOW
            </Link>
            <Link to="/contact" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-4 rounded-full font-medium text-sm transition-colors">
              BECOME A VOLUNTEER
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
